import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronRight, Gavel, Plus, RefreshCw, Search, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { DEFAULT_VIEW, VIEW_TABS, canExportGovernance, canProposeDecision, formatDay, governanceError } from "../lib/governance";
import { DecisionState } from "../components/governance/GovernanceBits";
import ExportButton from "../components/governance/ExportButton";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const EMPTY_TEXT = {
  to_adopt: ["Rien ne vous attend", "Les décisions où vous êtes décideur, proposées par quelqu'un d'autre, apparaissent ici."],
  undecided: ["Aucune décision en attente", "Une décision proposée ou différée reste ici jusqu'à ce qu'elle soit tranchée."],
  adopted: ["Aucune décision adoptée", "Les décisions adoptées forment la mémoire de l'entreprise."],
  all: ["Aucune décision pour le moment", "Consignez ce qui est décidé, par qui et pourquoi : plus rien ne se perd dans les conversations."],
};

function DateCell({ decision }) {
  if (decision.status === "adopted" && decision.decided_on) return <>Décidée le {formatDay(decision.decided_on)}</>;
  if (decision.status === "deferred" && decision.review_on) return <>Revue le {formatDay(decision.review_on)}</>;
  return <>Proposée le {formatDay(decision.created_at)}</>;
}

export default function GovernanceDecisions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = VIEW_TABS.some(([id]) => id === params.get("view")) ? params.get("view") : DEFAULT_VIEW;
  const search = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const seq = useRef(0);
  const canPropose = canProposeDecision(user);

  const update = (changes) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(changes)) { if (v) next.set(k, v); else next.delete(k); }
    return next;
  }, { replace: true });
  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== search) update({ q: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase((p) => (p === "ready" ? p : "loading"));
    try {
      const { data: body } = await api.get("/governance/decisions", { params: { view, limit: 200, ...(search ? { q: search } : {}) } });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(governanceError(e, "Impossible de charger les décisions."));
      setPhase("error");
    }
  }, [view, search]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const reset = () => { setSearchInput(""); update({ q: "" }); };
  const open = (d) => navigate(`/gouvernance/decisions/${d.id}`);

  return (
    <div className="space-y-4" data-testid="governance-decisions-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Décisions</h1>
          <p className="text-sm text-slate-500">Ce qui a été décidé, par qui et pourquoi. Une décision différée reste visible comme « non tranchée » et ne déclenche rien.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canExportGovernance(user) && <ExportButton kind="decisions" />}
          {canPropose && <Button asChild data-testid="decision-new"><Link to="/gouvernance/decisions/nouvelle"><Plus size={16} className="mr-1.5" />Nouvelle décision</Link></Button>}
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0" role="tablist" aria-label="Décisions">
        {VIEW_TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => update({ view: id === DEFAULT_VIEW ? "" : id })} data-testid={`decisions-tab-${id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap shrink-0 ${view === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {label}{data.counts?.[id] != null && <span className={`ml-1.5 tabular-nums ${view === id ? "text-white/80" : "text-slate-400"}`} data-testid={`decisions-count-${id}`}>{data.counts[id]}</span>}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Numéro ou titre…" className="pl-9 h-9" aria-label="Rechercher une décision" data-testid="decisions-search" />
        </div>
        {search && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="decisions-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="decisions-empty">
          <Gavel className="mx-auto text-slate-300" size={26} />
          {search ? <><h3 className="font-medium text-slate-900">Aucune décision ne correspond</h3><Button variant="outline" size="sm" onClick={reset}>Effacer la recherche</Button></> : (
            <><h3 className="font-medium text-slate-900">{EMPTY_TEXT[view][0]}</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">{EMPTY_TEXT[view][1]}</p>
              {canPropose && view !== "to_adopt" && <Button asChild size="sm"><Link to="/gouvernance/decisions/nouvelle"><Plus size={14} className="mr-1.5" />Nouvelle décision</Link></Button>}</>
          )}
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="decisions-table">
            <Table>
              <TableHeader><TableRow><TableHead>Décision</TableHead><TableHead>État</TableHead><TableHead>Décideurs</TableHead><TableHead className="hidden lg:table-cell">Date</TableHead><TableHead className="hidden lg:table-cell text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((d) => (
                  <TableRow key={d.id} className="cursor-pointer" onClick={() => open(d)} data-testid={`decision-row-${d.number}`}>
                    <TableCell className="max-w-md"><div className="font-medium text-slate-900 truncate">{d.title}</div><div className="text-xs text-slate-500 tabular-nums">{d.number}</div></TableCell>
                    <TableCell><DecisionState decision={d} testid={`decision-state-${d.number}`} /></TableCell>
                    <TableCell className="text-sm">{d.deciders.length ? d.deciders.map((p) => p.name).join(", ") : <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-slate-600"><DateCell decision={d} /></TableCell>
                    <TableCell className="hidden lg:table-cell text-right text-sm tabular-nums">{d.tasks_count || <span className="text-slate-400">—</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="decisions-cards">
            {data.items.map((d) => (
              <button key={d.id} type="button" onClick={() => open(d)} className="flex w-full items-start gap-3 px-4 py-3 text-left" data-testid={`decision-card-${d.number}`}>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="font-medium text-slate-900">{d.title}</div>
                  <div className="text-xs text-slate-500">{d.number} · <DateCell decision={d} /></div>
                  <DecisionState decision={d} testid={`decision-card-state-${d.number}`} />
                  {d.deciders.length > 0 && <div className="text-xs text-slate-600">Décideurs : {d.deciders.map((p) => p.name).join(", ")}</div>}
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
              </button>
            ))}
          </Card>
          <p className="text-xs text-slate-500" data-testid="decisions-total">{data.total} décision{data.total > 1 ? "s" : ""}</p>
        </>
      )}
    </div>
  );
}
